import { useState, useEffect, useCallback } from 'react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { getStripe } from '../../../lib/stripe';
import { motion, AnimatePresence } from 'framer-motion';

// ─────────────────────────────────────────────
// 型定義
// ─────────────────────────────────────────────
interface TicketPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseComplete: (purchasedCount: number) => void;
  eventId: string;
  eventTitle: string;
  ticketPrice: number;
  maxCapacity: number;
  currentReservations: number;
  displayColor?: string;
  cancelPolicy?: '24h' | '7d' | 'no_refund';
}

interface FreeSlotInfo {
  hostType: 'node' | 'artist';
  totalSlots: number;
  usedSlots: number;
  remainingSlots: number;
}

// ─────────────────────────────────────────────
// 定数
// ─────────────────────────────────────────────
const CANCEL_POLICY_MAP: Record<string, { label: string; desc: string; icon: string; color: string }> = {
  '24h': {
    label: '開催24時間前まで返金可',
    desc: 'イベント開始の24時間前までキャンセル・全額返金に対応します。',
    icon: 'ri-time-line',
    color: '#22d3ee',
  },
  '7d': {
    label: '7日前まで返金可',
    desc: 'イベント開始の7日前までキャンセル・全額返金に対応します。',
    icon: 'ri-calendar-line',
    color: '#22d3ee',
  },
  'no_refund': {
    label: '原則返金不可',
    desc: '購入後のキャンセル・返金には原則対応しません。ご注意ください。',
    icon: 'ri-close-circle-line',
    color: '#f87171',
  },
};

// ─────────────────────────────────────────────
// ステップ1: チケット選択・金額確認パネル
// ─────────────────────────────────────────────
interface SelectionPanelProps {
  ticketCount: number;
  setTicketCount: (n: number) => void;
  resonateAmount: number;
  setResonateAmount: (n: number) => void;
  availableTickets: number;
  ticketPrice: number;
  freeSlotInfo: FreeSlotInfo | null;
  isLoadingSlots: boolean;
  hasFreeSlot: boolean;
  systemFee: number;
  totalAmount: number;
  policyAgreed: boolean;
  setPolicyAgreed: (v: boolean) => void;
  cancelPolicy: '24h' | '7d' | 'no_refund';
  errorMessage: string | null;
  setErrorMessage: (v: string | null) => void;
  onNext: () => void;
  isCreatingIntent: boolean;
}

function SelectionPanel({
  ticketCount, setTicketCount,
  resonateAmount, setResonateAmount,
  availableTickets, ticketPrice,
  freeSlotInfo, isLoadingSlots, hasFreeSlot, systemFee,
  totalAmount, policyAgreed, setPolicyAgreed,
  cancelPolicy, errorMessage, setErrorMessage,
  onNext, isCreatingIntent,
}: SelectionPanelProps) {
  const totalTicketPrice = ticketPrice * ticketCount;
  const expectedExp = Math.floor(resonateAmount / 1000);
  const policyInfo = CANCEL_POLICY_MAP[cancelPolicy] ?? CANCEL_POLICY_MAP['24h'];

  return (
    <div className="space-y-5">
      {/* チケット枚数 */}
      <div
        className="p-[0.809rem] rounded-xl border"
        style={{ background: 'rgba(255,255,255,0.02)', backdropFilter: 'blur(12px)', borderColor: 'rgba(255,255,255,0.07)' }}
      >
        <label className="block text-sm font-medium text-cyan-400 mb-3">チケット枚数</label>
        <div className="flex items-center gap-4">
          <motion.button
            onClick={() => setTicketCount(Math.max(1, ticketCount - 1))}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 transition-colors cursor-pointer"
          >
            <i className="ri-subtract-line text-cyan-400" />
          </motion.button>
          <div className="flex-1 text-center">
            <motion.span
              key={ticketCount}
              initial={{ scale: 1.2, opacity: 0.6 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-3xl font-bold text-white"
            >
              {ticketCount}
            </motion.span>
          </div>
          <motion.button
            onClick={() => setTicketCount(Math.min(availableTickets, ticketCount + 1))}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 transition-colors cursor-pointer"
          >
            <i className="ri-add-line text-cyan-400" />
          </motion.button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center tracking-wider">残り {availableTickets} 枚</p>
      </div>

      {/* 料金内訳 */}
      <div
        className="space-y-3 p-[0.809rem] rounded-xl border"
        style={{ background: 'rgba(34,211,238,0.04)', backdropFilter: 'blur(12px)', borderColor: 'rgba(34,211,238,0.18)' }}
      >
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-300">チケット代</span>
          <span className="text-lg font-bold text-white">¥{totalTicketPrice.toLocaleString()}</span>
        </div>
        <div className="flex justify-between items-start">
          <span className="text-sm text-gray-300">システム手数料</span>
          <div className="text-right">
            {isLoadingSlots ? (
              <div className="flex items-center gap-1.5">
                <i className="ri-loader-4-line animate-spin text-cyan-400 text-sm" />
                <span className="text-sm text-gray-400">確認中...</span>
              </div>
            ) : hasFreeSlot ? (
              <div>
                <div className="flex items-center gap-2 justify-end">
                  <span className="text-base font-bold text-gray-500 line-through">¥{Math.floor(totalTicketPrice * 0.1).toLocaleString()}</span>
                  <span className="text-lg font-bold text-white">¥0</span>
                </div>
                <div className="text-xs text-green-400 mt-1 animate-pulse">🎁 主催者サブスク特典により手数料無料！</div>
              </div>
            ) : (
              <span className="text-lg font-bold text-white">¥{systemFee.toLocaleString()}</span>
            )}
          </div>
        </div>

        {!isLoadingSlots && freeSlotInfo && (
          <div className="pt-2 border-t border-cyan-500/20">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-cyan-400/70">主催者の無料枠</span>
              <div className="flex items-center gap-1">
                <span className="text-xs font-bold text-cyan-300">{freeSlotInfo.remainingSlots}</span>
                <span className="text-xs text-gray-500">/ {freeSlotInfo.totalSlots} 残</span>
              </div>
            </div>
            <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(freeSlotInfo.remainingSlots / freeSlotInfo.totalSlots) * 100}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="h-full rounded-full"
                style={{ background: freeSlotInfo.remainingSlots > 0 ? 'linear-gradient(to right, #22d3ee, #a855f7)' : '#ef4444' }}
              />
            </div>
            {freeSlotInfo.remainingSlots === 0 && <p className="text-xs text-red-400 mt-1">今月の無料枠は使い切りました</p>}
          </div>
        )}
      </div>

      {/* Resonate上乗せ */}
      <div
        className="space-y-4 p-[0.809rem] rounded-xl border"
        style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.08), rgba(236,72,153,0.06))', backdropFilter: 'blur(12px)', borderColor: 'rgba(168,85,247,0.25)' }}
      >
        <div className="flex items-center gap-2">
          <i className="ri-fire-fill text-xl text-orange-400" />
          <h3 className="text-sm font-bold text-white">推しをブーストしてトップサポーターになる</h3>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-300">支援額（Resonate）</span>
            <motion.span
              key={resonateAmount}
              initial={{ scale: 1.1, opacity: 0.7 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400"
            >
              +¥{resonateAmount.toLocaleString()}
            </motion.span>
          </div>
          <input
            type="range"
            min="0"
            max="50000"
            step="1000"
            value={resonateAmount}
            onChange={(e) => setResonateAmount(Number(e.target.value))}
            className="w-full h-2 bg-gray-800 rounded-lg appearance-none cursor-pointer slider-thumb"
            style={{ background: `linear-gradient(to right, #a855f7 0%, #ec4899 ${(resonateAmount / 50000) * 100}%, #1f2937 ${(resonateAmount / 50000) * 100}%, #1f2937 100%)` }}
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>¥0</span>
            <span>¥50,000</span>
          </div>
          <AnimatePresence>
            {resonateAmount > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="text-center p-[0.618rem] rounded-xl border"
                style={{ background: 'rgba(168,85,247,0.08)', borderColor: 'rgba(168,85,247,0.25)' }}
              >
                <div className="text-xs text-purple-300 mb-1">獲得予定EXP</div>
                <div className="text-2xl font-bold text-purple-400">+{expectedExp} EXP</div>
                <div className="text-xs text-gray-500 mt-1">1,000円 = 1 EXP</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* 最終決済額 */}
      <div
        className="p-[0.809rem] rounded-xl border-2"
        style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.12), rgba(168,85,247,0.10))', backdropFilter: 'blur(16px)', borderColor: 'rgba(34,211,238,0.45)', boxShadow: '0 0 30px rgba(34,211,238,0.12)' }}
      >
        <div className="text-sm text-cyan-300 mb-2 tracking-wider">最終決済額</div>
        <motion.div
          key={totalAmount}
          initial={{ scale: 1.05, opacity: 0.7 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-4xl font-bold text-white mb-3"
        >
          ¥{totalAmount.toLocaleString()}
        </motion.div>
        <div className="space-y-1 text-xs text-gray-400">
          <div className="flex justify-between">
            <span>チケット代</span>
            <span>¥{totalTicketPrice.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span>システム手数料</span>
            <span className={hasFreeSlot ? 'text-green-400' : ''}>¥{systemFee.toLocaleString()}</span>
          </div>
          {resonateAmount > 0 && (
            <div className="flex justify-between text-purple-400">
              <span>Resonate支援</span>
              <span>¥{resonateAmount.toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      {/* 販売主体告知 */}
      <div className="rounded-xl overflow-hidden" style={{ border: '2px solid rgba(251,191,36,0.7)', background: 'rgba(251,191,36,0.05)', backdropFilter: 'blur(12px)', boxShadow: '0 0 24px rgba(251,191,36,0.18)' }}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-yellow-500/30 bg-yellow-500/10">
          <div className="w-5 h-5 flex items-center justify-center"><i className="ri-alert-fill text-sm text-yellow-400" /></div>
          <span className="text-yellow-300 text-xs font-bold tracking-wider">⚠ 販売主体に関する重要事項</span>
          <span className="ml-auto px-2 py-0.5 rounded-full bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 text-[9px] font-bold tracking-wider">必読</span>
        </div>
        <div className="px-4 py-3 space-y-2">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-yellow-500/8 border border-yellow-500/25">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-500/15 border border-yellow-500/40 flex-shrink-0 mt-0.5">
              <i className="ri-store-2-line text-sm text-yellow-400" />
            </div>
            <div>
              <p className="text-yellow-300 text-xs font-bold mb-1">販売者：Maker / Node（イベント主催者）</p>
              <p className="text-gray-300 text-xs leading-relaxed">本チケットの販売者は、Dig-Dim上で活動する<strong className="text-yellow-300"> Maker / Node（イベント主催者）</strong>です。Dig-Dim運営は<strong className="text-white">決済処理の代行者</strong>であり、売買契約の当事者ではありません。</p>
            </div>
          </div>
          <div className="flex items-start gap-2 p-2.5 rounded-lg bg-white/3 border border-white/8">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-bank-card-line text-xs text-gray-400" /></div>
            <p className="text-gray-500 text-[10px] leading-relaxed">クレジットカードの明細に「Dig-Dim」と表示される場合がありますが、実際の販売者はイベント主催者（Maker/Node）です。</p>
          </div>
        </div>
      </div>

      {/* キャンセルポリシー */}
      <div className="rounded-xl border-2 overflow-hidden" style={{ borderColor: 'rgba(236,72,153,0.8)', background: 'rgba(236,72,153,0.03)', backdropFilter: 'blur(12px)', boxShadow: '0 0 20px rgba(236,72,153,0.12)' }}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-pink-500/30 bg-pink-500/10">
          <div className="w-5 h-5 flex items-center justify-center"><i className="ri-refund-2-line text-sm text-pink-400" /></div>
          <span className="text-pink-400 text-xs font-bold tracking-wider">キャンセルポリシー</span>
          <span className="ml-auto px-2 py-0.5 rounded-full bg-pink-500/20 border border-pink-500/40 text-pink-400 text-[9px] font-bold tracking-wider">要確認</span>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-start gap-3 p-3 rounded-lg border" style={{ background: cancelPolicy === 'no_refund' ? 'rgba(248,113,113,0.08)' : 'rgba(34,211,238,0.06)', borderColor: cancelPolicy === 'no_refund' ? 'rgba(248,113,113,0.3)' : 'rgba(34,211,238,0.2)' }}>
            <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5" style={{ background: cancelPolicy === 'no_refund' ? 'rgba(248,113,113,0.15)' : 'rgba(34,211,238,0.15)' }}>
              <i className={`${policyInfo.icon} text-base`} style={{ color: policyInfo.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold mb-1" style={{ color: policyInfo.color }}>{policyInfo.label}</p>
              <p className="text-gray-400 text-xs leading-relaxed">{policyInfo.desc}</p>
            </div>
          </div>
          <div className="flex items-start gap-2 p-3 rounded-lg bg-green-500/5 border border-green-500/20">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-shield-check-line text-sm text-green-400" /></div>
            <p className="text-green-300 text-[10px] leading-relaxed">主催者都合によるイベント中止・中断の場合は、プラットフォームを通じて<strong>全額返金</strong>が保証されます。</p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer group pt-1">
            <div className="relative mt-0.5 flex-shrink-0">
              <input type="checkbox" checked={policyAgreed} onChange={(e) => { setPolicyAgreed(e.target.checked); if (e.target.checked) setErrorMessage(null); }} className="sr-only" />
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${policyAgreed ? 'bg-pink-500 border-pink-500' : 'bg-transparent border-pink-500/60 group-hover:border-pink-400'}`}>
                {policyAgreed && <i className="ri-check-line text-xs text-white" />}
              </div>
            </div>
            <p className={`text-xs leading-relaxed ${policyAgreed ? 'text-pink-300' : 'text-gray-400'}`}>上記のキャンセルポリシーを確認・同意した上で購入します。</p>
          </label>
        </div>
      </div>

      {errorMessage && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/40 rounded-xl"
        >
          <i className="ri-error-warning-line text-red-400 text-base mt-0.5 w-5 h-5 flex items-center justify-center shrink-0" />
          <p className="text-xs text-red-300">{errorMessage}</p>
        </motion.div>
      )}

      <motion.button
        onClick={onNext}
        disabled={isCreatingIntent || isLoadingSlots || !policyAgreed}
        whileHover={!isCreatingIntent && !isLoadingSlots && policyAgreed ? { scale: 1.02 } : {}}
        whileTap={!isCreatingIntent && !isLoadingSlots && policyAgreed ? { scale: 0.98 } : {}}
        className="whitespace-nowrap w-full py-[0.809rem] text-white font-bold text-lg rounded-xl hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        style={{
          background: 'linear-gradient(135deg, #22d3ee, #a855f7)',
          boxShadow: '0 0 30px rgba(34,211,238,0.25), 0 4px 20px rgba(168,85,247,0.2)',
        }}
      >
        {isCreatingIntent ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin" />
            決済を準備中...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-secure-payment-line" />
            お支払い方法を選択する
            <i className="ri-arrow-right-line" />
          </span>
        )}
      </motion.button>
    </div>
  );
}

// ─────────────────────────────────────────────
// ステップ2: Stripe PaymentElement 決済パネル
// ─────────────────────────────────────────────
interface PaymentPanelProps {
  totalAmount: number;
  ticketCount: number;
  resonateAmount: number;
  systemFee: number;
  hasFreeSlot: boolean;
  ticketPrice: number;
  onBack: () => void;
  onSuccess: () => void;
  eventId: string;
  userId: string;
  cancelPolicy: '24h' | '7d' | 'no_refund';
}

function PaymentPanelInner({
  totalAmount, ticketCount, resonateAmount, systemFee, hasFreeSlot,
  ticketPrice, onBack, onSuccess, eventId, userId, cancelPolicy,
}: PaymentPanelProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showNoRefundWarning, setShowNoRefundWarning] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);

  const totalTicketPrice = ticketPrice * ticketCount;

  const executeConfirm = useCallback(async () => {
    if (!stripe || !elements || !user) return;
    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // ① Stripe 決済を確定
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (stripeError) {
        setErrorMessage(stripeError.message ?? '決済に失敗しました。もう一度お試しください。');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status !== 'succeeded') {
        setErrorMessage('決済が完了しませんでした。もう一度お試しください。');
        setIsProcessing(false);
        return;
      }

      // ② 決済成功後に secure_event_reservation RPC で予約確定
      const { data: rpcResult, error: rpcError } = await supabase.rpc(
        'secure_event_reservation',
        {
          p_event_id: eventId,
          p_user_id: userId,
          p_ticket_count: ticketCount,
          p_resonate_amount: resonateAmount,
        }
      );

      if (rpcError) throw rpcError;

      const result = rpcResult as { success: boolean; error?: string };
      if (!result?.success) {
        throw new Error(result?.error ?? '予約の確定に失敗しました。サポートにお問い合わせください。');
      }

      // ③ Resonate 記録
      if (resonateAmount > 0) {
        const { data: eventData } = await supabase
          .from('events')
          .select('artist_id')
          .eq('id', eventId)
          .maybeSingle();

        if (eventData?.artist_id) {
          await supabase.from('resonance_payments').insert({
            seeker_id: userId,
            artist_id: eventData.artist_id,
            amount: resonateAmount,
            stripe_payment_intent_id: paymentIntent.id,
            status: 'completed',
          });
        }
      }

      // ④ チケット確認メール送信
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;

        const { data: eventInfo } = await supabase
          .from('events')
          .select('title, start_date, venue_name')
          .eq('id', eventId)
          .maybeSingle();

        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
        await fetch(`${supabaseUrl}/functions/v1/send-ticket-confirmation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            user_id: userId,
            event_id: eventId,
            event_title: eventInfo?.title ?? '',
            event_date: eventInfo?.start_date ?? '',
            event_venue: eventInfo?.venue_name ?? '',
            ticket_count: ticketCount,
            total_amount: totalAmount,
          }),
        });
      } catch (mailErr) {
        // メール送信失敗は予約自体には影響させない（サイレント失敗）
        console.warn('メール送信に失敗しました:', mailErr);
      }

      onSuccess();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '予約処理に失敗しました。もう一度お試しください。';
      setErrorMessage(msg);
    } finally {
      setIsProcessing(false);
    }
  }, [stripe, elements, user, eventId, userId, ticketCount, resonateAmount, totalAmount, onSuccess]);

  const handlePay = () => {
    if (cancelPolicy === 'no_refund') {
      setShowNoRefundWarning(true);
    } else {
      executeConfirm();
    }
  };

  return (
    <>
      {/* 返金不可 警告ダイアログ */}
      {showNoRefundWarning && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 z-20 flex items-center justify-center p-6 rounded-xl overflow-hidden"
          style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(20px)' }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 280, damping: 22 }}
            className="w-full max-w-sm rounded-2xl border-2 border-red-500 overflow-hidden"
            style={{ background: 'rgba(10,0,0,0.97)', boxShadow: '0 0 50px rgba(239,68,68,0.3)' }}
          >
            <div className="flex flex-col items-center pt-8 pb-4 px-6">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-500/15 border-2 border-red-500/60 mb-4 animate-pulse">
                <i className="ri-error-warning-fill text-3xl text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-white tracking-wider mb-1">返金不可のご確認</h3>
              <p className="text-xs text-red-400 font-semibold tracking-widest">NO REFUND POLICY</p>
            </div>
            <div className="mx-6 mb-5 p-4 rounded-xl bg-red-500/8 border border-red-500/30">
              <p className="text-sm text-gray-200 leading-relaxed text-center">
                このイベントのキャンセルポリシーは<br />
                <strong className="text-red-400">「原則返金不可」</strong>です。<br /><br />
                購入後のキャンセル・返金には<br />対応できません。<br />
                <span className="text-xs text-gray-400 mt-2 block">※ 主催者都合による中止の場合のみ全額返金されます。</span>
              </p>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setShowNoRefundWarning(false)} className="whitespace-nowrap flex-1 py-3 rounded-xl border border-gray-600 text-gray-300 text-sm font-semibold hover:bg-gray-800 transition-colors cursor-pointer">キャンセル</button>
              <button onClick={() => { setShowNoRefundWarning(false); executeConfirm(); }} className="whitespace-nowrap flex-1 py-3 rounded-xl bg-red-500 hover:bg-red-400 text-white text-sm font-bold transition-colors cursor-pointer" style={{ boxShadow: '0 0 20px rgba(239,68,68,0.35)' }}>理解して購入する</button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <div className="space-y-5">
        {/* 金額サマリー */}
        <div
          className="p-[0.809rem] rounded-xl border"
          style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.10), rgba(168,85,247,0.08))', backdropFilter: 'blur(16px)', borderColor: 'rgba(34,211,238,0.35)', boxShadow: '0 0 24px rgba(34,211,238,0.10)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-cyan-300 font-medium tracking-wider">お支払い合計</span>
            <span className="text-3xl font-bold text-white">¥{totalAmount.toLocaleString()}</span>
          </div>
          <div className="space-y-1 text-xs text-gray-400 border-t border-cyan-500/20 pt-2">
            <div className="flex justify-between">
              <span>チケット代 × {ticketCount}枚</span>
              <span>¥{(ticketPrice * ticketCount).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span>システム手数料</span>
              <span className={hasFreeSlot ? 'text-green-400' : ''}>¥{systemFee.toLocaleString()}</span>
            </div>
            {resonateAmount > 0 && (
              <div className="flex justify-between text-purple-400">
                <span>Resonate支援</span>
                <span>¥{resonateAmount.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>

        {/* 販売主体告知（決済直前） */}
        <div className="rounded-xl overflow-hidden" style={{ border: '2px solid rgba(251,191,36,0.8)', background: 'rgba(251,191,36,0.05)', backdropFilter: 'blur(12px)', boxShadow: '0 0 24px rgba(251,191,36,0.18)' }}>
          <div className="flex items-center gap-2 px-4 py-2.5 bg-yellow-500/15 border-b border-yellow-500/30">
            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-alert-fill text-yellow-400 text-sm" /></div>
            <span className="text-yellow-300 text-xs font-bold tracking-wider">決済前の重要確認事項</span>
          </div>
          <div className="px-4 py-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-yellow-500/20 border border-yellow-500/50 flex-shrink-0 mt-0.5">
                <i className="ri-store-2-fill text-yellow-400 text-sm" />
              </div>
              <div>
                <p className="text-yellow-200 text-xs font-bold mb-1.5">販売者はMaker / Node（イベント主催者）です</p>
                <p className="text-gray-300 text-xs leading-relaxed">Dig-Dim運営は<strong className="text-white">決済代行者</strong>であり、売買契約の当事者ではありません。</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stripe PaymentElement */}
        <div
          className="rounded-xl overflow-hidden border p-[0.809rem]"
          style={{ background: 'rgba(255,255,255,0.02)', backdropFilter: 'blur(12px)', borderColor: 'rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-bank-card-line text-cyan-400 text-sm" /></div>
            <span className="text-sm font-medium text-white">お支払い方法</span>
            <div className="ml-auto flex items-center gap-1.5">
              <i className="ri-apple-fill text-gray-400 text-xs" />
              <i className="ri-google-fill text-gray-400 text-xs" />
              <span className="text-gray-500 text-[10px]">など対応</span>
            </div>
          </div>
          <PaymentElement
            onReady={() => setPaymentReady(true)}
            options={{ layout: 'tabs', wallets: { applePay: 'auto', googlePay: 'auto' } }}
          />
        </div>

        {/* セキュリティバッジ */}
        <div className="flex items-center justify-center gap-3 text-gray-500 text-xs">
          <div className="flex items-center gap-1">
            <i className="ri-lock-line text-green-500 text-sm" />
            <span>SSL暗号化通信</span>
          </div>
          <span>·</span>
          <div className="flex items-center gap-1">
            <i className="ri-shield-check-line text-green-500 text-sm" />
            <span>Stripe セキュア決済</span>
          </div>
        </div>

        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/40 rounded-xl"
          >
            <i className="ri-error-warning-line text-red-400 text-base mt-0.5 w-5 h-5 flex items-center justify-center shrink-0" />
            <p className="text-xs text-red-300">{errorMessage}</p>
          </motion.div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onBack}
            disabled={isProcessing}
            className="whitespace-nowrap px-5 py-[0.809rem] border border-white/10 text-gray-300 font-semibold rounded-xl hover:bg-white/5 transition-colors disabled:opacity-50 cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-1" />
            戻る
          </button>
          <motion.button
            onClick={handlePay}
            disabled={isProcessing || !stripe || !elements || !paymentReady}
            whileHover={!isProcessing && stripe && elements && paymentReady ? { scale: 1.02 } : {}}
            whileTap={!isProcessing && stripe && elements && paymentReady ? { scale: 0.98 } : {}}
            className="whitespace-nowrap flex-1 py-[0.809rem] text-white font-bold text-lg rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            style={{
              background: 'linear-gradient(135deg, #22d3ee, #a855f7)',
              boxShadow: '0 0 30px rgba(34,211,238,0.25), 0 4px 20px rgba(168,85,247,0.2)',
            }}
          >
            {isProcessing ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin" />
                決済処理中...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-secure-payment-line" />
                ¥{totalAmount.toLocaleString()} を支払う
              </span>
            )}
          </motion.button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
// 成功画面コンポーネント（祝福演出強化版）
// ─────────────────────────────────────────────
function TicketSuccessView({
  ticketCount,
  totalAmount,
  eventTitle,
}: {
  ticketCount: number;
  totalAmount: number;
  eventTitle: string;
}) {
  const particles = Array.from({ length: 24 }, (_, i) => {
    const angle = (i / 24) * Math.PI * 2;
    const radius = 48 + (i % 4) * 14;
    const colors = ['#22d3ee', '#a855f7', '#ec4899', '#ffffff', '#34d399'];
    return {
      angle,
      radius,
      color: colors[i % colors.length],
      size: i % 5 === 0 ? 3.5 : i % 3 === 0 ? 2.5 : 1.8,
      delay: i * 0.035,
    };
  });

  const sparkles = Array.from({ length: 10 }, (_, i) => ({
    x: 10 + (i * 8.5) % 80,
    y: 8 + (i * 11) % 84,
    delay: 0.25 + i * 0.1,
    color: i % 2 === 0 ? '#22d3ee' : '#a855f7',
  }));

  return (
    <div className="flex flex-col items-center justify-center py-14 px-6 relative overflow-hidden">
      {/* 背景グロー */}
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, ease: 'easeOut' }}
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(34,211,238,0.10) 0%, rgba(168,85,247,0.06) 40%, transparent 70%)' }}
      />

      {/* パーティクル */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0, x: '50%', y: '38%' }}
            animate={{
              opacity: [0, 1, 0.9, 0],
              scale: [0, 1.3, 1, 0.3],
              x: `calc(50% + ${Math.cos(p.angle) * p.radius}%)`,
              y: `calc(38% + ${Math.sin(p.angle) * p.radius}%)`,
            }}
            transition={{ duration: 1.5, delay: p.delay, ease: [0.2, 0.8, 0.4, 1] }}
            className="absolute rounded-full"
            style={{ width: p.size, height: p.size, background: p.color, boxShadow: `0 0 ${p.size * 3}px ${p.color}`, left: 0, top: 0 }}
          />
        ))}
        {sparkles.map((s, i) => (
          <motion.div
            key={`sp-${i}`}
            initial={{ opacity: 0, scale: 0, rotate: 0 }}
            animate={{ opacity: [0, 1, 0], scale: [0, 1, 0], rotate: [0, 180] }}
            transition={{ duration: 0.85, delay: s.delay, ease: 'easeOut' }}
            className="absolute"
            style={{ left: `${s.x}%`, top: `${s.y}%` }}
          >
            <i className="ri-sparkling-2-fill text-sm" style={{ color: s.color }} />
          </motion.div>
        ))}
      </div>

      {/* チェックアイコン */}
      <motion.div
        initial={{ scale: 0, rotate: -25 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 16, delay: 0.06 }}
        className="w-24 h-24 flex items-center justify-center rounded-2xl mb-6 relative"
        style={{
          background: 'linear-gradient(135deg, rgba(34,211,238,0.20), rgba(168,85,247,0.12))',
          border: '2px solid rgba(34,211,238,0.50)',
          boxShadow: '0 0 60px rgba(34,211,238,0.28), 0 0 120px rgba(168,85,247,0.12)',
        }}
      >
        <motion.i
          initial={{ scale: 0, rotate: 15 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.22, type: 'spring', stiffness: 360, damping: 14 }}
          className="ri-check-line text-5xl text-cyan-400"
        />
        {/* リング1 */}
        <motion.div
          initial={{ scale: 0.85, opacity: 1 }}
          animate={{ scale: 2.4, opacity: 0 }}
          transition={{ duration: 1.1, delay: 0.28, ease: 'easeOut' }}
          className="absolute inset-0 rounded-2xl border-2 border-cyan-400 pointer-events-none"
        />
        {/* リング2 */}
        <motion.div
          initial={{ scale: 0.85, opacity: 0.7 }}
          animate={{ scale: 3.0, opacity: 0 }}
          transition={{ duration: 1.4, delay: 0.45, ease: 'easeOut' }}
          className="absolute inset-0 rounded-2xl border border-purple-400 pointer-events-none"
        />
      </motion.div>

      {/* テキスト */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
        className="text-center mb-5"
      >
        <p className="text-2xl font-bold text-white tracking-wider mb-1">予約完了！</p>
        <p
          className="text-sm font-bold tracking-widest mb-2"
          style={{ color: '#22d3ee' }}
        >
          RESERVATION CONFIRMED
        </p>
        <p className="text-sm text-gray-400 tracking-wide">{eventTitle}</p>
      </motion.div>

      {/* 金額バッジ */}
      <motion.div
        initial={{ opacity: 0, scale: 0.75 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.46, type: 'spring', stiffness: 240, damping: 18 }}
        className="flex items-center gap-3 px-6 py-[0.618rem] rounded-full mb-4"
        style={{
          background: 'linear-gradient(135deg, rgba(34,211,238,0.12), rgba(168,85,247,0.10))',
          border: '1px solid rgba(34,211,238,0.35)',
          boxShadow: '0 0 20px rgba(34,211,238,0.15)',
        }}
      >
        <i className="ri-ticket-2-line text-cyan-400" />
        <span className="text-white font-bold text-sm">{ticketCount}枚</span>
        <span className="text-gray-500 text-xs">·</span>
        <span className="text-cyan-400 font-bold text-sm">¥{totalAmount.toLocaleString()}</span>
      </motion.div>

      {/* メール確認 */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.62 }}
        className="text-xs text-gray-500 tracking-wider mb-5"
      >
        確認メールをご確認ください
      </motion.p>

      {/* 販売主体告知 */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.72 }}
        className="w-full max-w-xs px-4 py-3 rounded-xl border text-center"
        style={{ borderColor: 'rgba(251,191,36,0.45)', background: 'rgba(251,191,36,0.06)', backdropFilter: 'blur(8px)' }}
      >
        <p className="text-yellow-300 text-xs leading-relaxed">
          <span className="font-bold text-yellow-400">販売者はMaker/Nodeです</span><br />
          Dig-Dim運営は決済代行者であり、<br />売買契約の当事者ではありません。
        </p>
      </motion.div>
    </div>
  );
}

// ─────────────────────────────────────────────
// メインモーダル
// ─────────────────────────────────────────────
export function TicketPurchaseModal({
  isOpen,
  onClose,
  onPurchaseComplete,
  eventId,
  eventTitle,
  ticketPrice,
  maxCapacity,
  currentReservations,
  displayColor = '#22d3ee',
  cancelPolicy = '24h',
}: TicketPurchaseModalProps) {
  const { user } = useAuth();

  type Step = 'select' | 'payment' | 'success';
  const [step, setStep] = useState<Step>('select');
  const [ticketCount, setTicketCount] = useState(1);
  const [resonateAmount, setResonateAmount] = useState(0);
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [freeSlotInfo, setFreeSlotInfo] = useState<FreeSlotInfo | null>(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(true);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);

  const availableTickets = Math.max(0, maxCapacity - currentReservations);
  const totalTicketPrice = ticketPrice * ticketCount;
  const hasFreeSlot = freeSlotInfo !== null && freeSlotInfo.remainingSlots > 0;
  const systemFee = hasFreeSlot ? 0 : Math.floor(totalTicketPrice * 0.1);
  const totalAmount = totalTicketPrice + systemFee + resonateAmount;

  const fetchFreeSlots = useCallback(async () => {
    setIsLoadingSlots(true);
    try {
      const { data, error } = await supabase.rpc('get_event_host_free_slots', { p_event_id: eventId });
      if (error) throw error;
      setFreeSlotInfo(data as FreeSlotInfo);
    } catch {
      setFreeSlotInfo(null);
    } finally {
      setIsLoadingSlots(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (isOpen && eventId) {
      setStep('select');
      setTicketCount(1);
      setResonateAmount(0);
      setPolicyAgreed(false);
      setErrorMessage(null);
      setClientSecret(null);
      fetchFreeSlots();
    }
  }, [isOpen, eventId, fetchFreeSlots]);

  const handleProceedToPayment = async () => {
    if (!user) return;
    if (!policyAgreed) { setErrorMessage('キャンセルポリシーへの同意が必要です。'); return; }
    if (ticketCount > availableTickets) { setErrorMessage('選択枚数が残席数を超えています。'); return; }

    const { data: existing } = await supabase.from('event_reservations').select('id').eq('event_id', eventId).eq('user_id', user.id).maybeSingle();
    if (existing) { setErrorMessage('このイベントはすでに予約済みです。'); return; }

    setIsCreatingIntent(true);
    setErrorMessage(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ amount: totalAmount, userId: user.id, type: 'ticket', eventId, metadata: { ticketCount: String(ticketCount), resonateAmount: String(resonateAmount), eventTitle } }),
      });
      const json = await res.json();
      if (!res.ok || !json.clientSecret) throw new Error(json.userMessage ?? json.error ?? '決済の準備に失敗しました。');
      setClientSecret(json.clientSecret);
      setStep('payment');
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : '決済の準備に失敗しました。');
    } finally {
      setIsCreatingIntent(false);
    }
  };

  const handlePaymentSuccess = async () => {
    await fetchFreeSlots();
    setStep('success');
    setTimeout(() => { onPurchaseComplete(ticketCount); }, 2200);
  };

  if (!isOpen) return null;

  const stripePromise = getStripe();
  const stepList: Step[] = ['select', 'payment', 'success'];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xl"
            onClick={step !== 'success' ? onClose : undefined}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 32, scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 300, damping: 26 }}
              className="relative w-full max-w-lg rounded-2xl overflow-hidden"
              style={{
                background: 'rgba(6, 6, 10, 0.96)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 0 80px rgba(34,211,238,0.12), 0 32px 64px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)',
              }}
            >
              {/* ヘッダー */}
              <div
                className="relative px-6 py-4 border-b"
                style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.08), rgba(168,85,247,0.06))', borderColor: 'rgba(255,255,255,0.07)' }}
              >
                {/* ステップインジケーター */}
                <div className="flex items-center gap-2 mb-2">
                  {stepList.map((s, i) => {
                    const currentIdx = stepList.indexOf(step);
                    const isDone = i < currentIdx;
                    const isActive = step === s;
                    return (
                      <div key={s} className="flex items-center gap-2">
                        <motion.div
                          animate={{
                            background: isActive ? 'linear-gradient(135deg, #22d3ee, #a855f7)' : isDone ? 'rgba(34,211,238,0.35)' : 'rgba(255,255,255,0.08)',
                            boxShadow: isActive ? '0 0 12px rgba(34,211,238,0.4)' : 'none',
                          }}
                          className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                          style={{ color: isActive ? '#000' : isDone ? '#22d3ee' : 'rgba(255,255,255,0.3)' }}
                        >
                          {isDone ? <i className="ri-check-line text-xs" /> : i + 1}
                        </motion.div>
                        {i < 2 && (
                          <div
                            className="w-8 h-0.5 rounded-full transition-all duration-500"
                            style={{ background: isDone ? 'rgba(34,211,238,0.5)' : 'rgba(255,255,255,0.08)' }}
                          />
                        )}
                      </div>
                    );
                  })}
                  <span className="ml-2 text-xs text-gray-500 tracking-wider">
                    {step === 'select' ? '内容確認' : step === 'payment' ? 'お支払い' : '完了'}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-white tracking-wider">TICKET RESERVATION</h2>
                <p className="text-sm text-cyan-400 mt-0.5 tracking-wide">{eventTitle}</p>
                {step !== 'success' && (
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/5 rounded-lg transition-all cursor-pointer"
                  >
                    <i className="ri-close-line text-xl" />
                  </button>
                )}
              </div>

              {/* コンテンツ */}
              <AnimatePresence mode="wait">
                {step === 'success' ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <TicketSuccessView
                      ticketCount={ticketCount}
                      totalAmount={totalAmount}
                      eventTitle={eventTitle}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.22 }}
                    className="p-6 max-h-[72vh] overflow-y-auto"
                  >
                    {step === 'select' && (
                      <SelectionPanel
                        ticketCount={ticketCount}
                        setTicketCount={setTicketCount}
                        resonateAmount={resonateAmount}
                        setResonateAmount={setResonateAmount}
                        availableTickets={availableTickets}
                        ticketPrice={ticketPrice}
                        freeSlotInfo={freeSlotInfo}
                        isLoadingSlots={isLoadingSlots}
                        hasFreeSlot={hasFreeSlot}
                        systemFee={systemFee}
                        totalAmount={totalAmount}
                        policyAgreed={policyAgreed}
                        setPolicyAgreed={setPolicyAgreed}
                        cancelPolicy={cancelPolicy}
                        errorMessage={errorMessage}
                        setErrorMessage={setErrorMessage}
                        onNext={handleProceedToPayment}
                        isCreatingIntent={isCreatingIntent}
                      />
                    )}
                    {step === 'payment' && clientSecret && (
                      <Elements
                        stripe={stripePromise}
                        options={{
                          clientSecret,
                          appearance: {
                            theme: 'night',
                            variables: {
                              colorPrimary: '#22d3ee',
                              colorBackground: '#0a0a0a',
                              colorText: '#ffffff',
                              colorDanger: '#f87171',
                              fontFamily: 'system-ui, sans-serif',
                              borderRadius: '10px',
                            },
                          },
                          locale: 'ja',
                        }}
                      >
                        <PaymentPanelInner
                          totalAmount={totalAmount}
                          ticketCount={ticketCount}
                          resonateAmount={resonateAmount}
                          systemFee={systemFee}
                          hasFreeSlot={hasFreeSlot}
                          ticketPrice={ticketPrice}
                          onBack={() => setStep('select')}
                          onSuccess={handlePaymentSuccess}
                          eventId={eventId}
                          userId={user?.id ?? ''}
                          cancelPolicy={cancelPolicy}
                        />
                      </Elements>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

export default TicketPurchaseModal;
